import React, { useState, useEffect } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import PropTypes from "prop-types";

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from 'assets/img/info.png';

// component
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import Table from "components/Table/Table.js";
import Button from 'components/CustomButtons/Button.js'
import UnityInterface from 'views/project/testcase/UnityInterface';

import SnackbarContent from "components/Snackbar/SnackbarContent.js";
import AddAlert from "@material-ui/icons/AddAlert";

import ToggleInput from 'views/component/ToggleInput';
import styles from 'views/component/Styles.js';
import { infoColor, whiteColor } from "assets/jss/material-dashboard-pro-react";

import CheckboxSelect from 'views/component/CheckboxSelect';
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FiberManualRecord from "@material-ui/icons/FiberManualRecord";
import { Typography } from "@material-ui/core";

const customStyle = {
    cardHeader: {
        backgroundColor: infoColor[0],
        color: whiteColor,
        padding: '0 1.25rem',
        '& > h4': { marginTop: '12px' }
    },
    underChartIcons: { fill: 'white' }
}
const useStyles = makeStyles(styles);
const customStyles = makeStyles(customStyle);

export default function ResultViewPage(props) {
    const { testcase, testcaseId } = props;
    const classes = { ...useStyles(), ...customStyles() };

    const timelineColor = ['#4caf50', '#0097a7', '#AAAAAA', '#FFcc29', '#ff9800', '#f44336'];
    const stateList = ['설정', '가동', 'OFF', '대기', '지연', '오류'];
    const LOCAL_STORAGE_NAME = 'GiGAtwinFactoryResultSetting' + testcaseId;
    const rawDefault = localStorage.getItem(LOCAL_STORAGE_NAME);
    const localDefault = rawDefault ? JSON.parse(rawDefault) : undefined;
    const chartList = ["상품 생산량", "생산 소요시간", "재공재고", "설비가동률", "설비상태", /*"설비부하율", "설비 Loss time",*/ "단위 제품당 처리시간"];

    const [kpiData, setKpiData] = useState({ outputAll: 0, wipAll: 0, productList: [] });

    const [result, setResult] = useState({});
    const [facilityList, setFacilityList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [selectedFacilityList, setSelectedFacilityList] = useState(localDefault && localDefault.selectedFacilityList ? localDefault.selectedFacilityList : []);
    const [selectedProductList, setSelectedProductList] = useState(localDefault && localDefault.selectedProductList ? localDefault.selectedProductList : []);
    const [selectedChartList, setSelectedChartList] = useState(localDefault && localDefault.selectedChartList ? localDefault.selectedChartList : []);
    const [rangeValue, setRangeValue] = useState(1);

    const [delayList, setDelayList] = useState([]);
    const [overWorkList, setOverWorkList] = useState([]);
    const [overCoefList, setOverCoefList] = useState([]);

    // chart Data
    const [resultFacility, setResultFacility] = useState([]);
    const [resultProduct, setResultProduct] = useState([]);
    const [resultState, setResultState] = useState([]);
    const [resultTime, setResultTime] = useState([]);

    // chart Data - details
    const [timelineData, setTimelineData] = useState([]);
    const [timelineColorOrder, setTimelineColorOrder] = useState(timelineColor);
    const [productData, setProductData] = useState([]);
    const [timeData, setTimeData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [wipData, setWipData] = useState([]);
    const [workRatio, setWorkRatio] = useState([]);
    const [loadFactor, setLoadFactor] = useState([]);
    const [lossTime, setLossTime] = useState([]);
    const [leadTime, setLeadTime] = useState([]);

    const [isUnityFull, setUnityFull] = useState(false);
    const [timelineHeight, setTimelineHeight] = useState(testcase.planList.length * 41 + 50);
    const noFacility = ['트랙', '분류', '워커', 'RGV'];

    useEffect(() => {
        if (testcaseId) {
            axios.get('data/result/' + testcaseId).then(response => {
                if (response.data.resultFacility) {
                    setResult(response.data);
                }
            });
        }
    }, [testcaseId]);
    useEffect(() => {
        if (!result.resultFacility) {
            return;
        }
        const facilityIds = testcase.facilityList.filter(item => !noFacility.includes(item.modelType)).map(item => item.facilityId);
        const resultFacility = result.resultFacility.filter(item => facilityIds.includes(item.facilityId));
        setResultFacility(resultFacility);
        setResultProduct(getResultProduct(result.resultProduct));
        setResultTime(getResultTime(result.resultTime));
        setResultState(result.resultState);
        let tempList = [];

        resultFacility.forEach(item => {
            if (!tempList.map(temp => temp.facilityId).includes(item.facilityId)) {
                tempList.push({ facilityId: item.facilityId, name: item.name });
            }
        });
        setFacilityList(tempList);
        const hasDefault = function (name) {
            return localDefault && localDefault[name] && localDefault[name].length !== 0;
        }
        if (hasDefault('selectedFacilityList')
            && localDefault.selectedFacilityList.filter(item => tempList.map(item => item.facilityId).includes(item)).length !== 0) {
            setSelectedFacilityList(localDefault.selectedFacilityList.filter(item => tempList.map(item => item.facilityId).includes(item)));
        } else {
            setSelectedFacilityList(tempList.map(item => item.facilityId));
        }

        const tempProductList = [...new Set(result.resultProduct.map(item => item.name))];
        setProductList(tempProductList);

        if (hasDefault('selectedProductList')
            && localDefault.selectedProductList.filter(item => tempProductList.includes(item)).length !== 0) {
            setSelectedProductList(localDefault.selectedProductList.filter(item => tempProductList.includes(item)));
        } else {
            setSelectedProductList(tempProductList);
        }

        hasDefault('selectedChartList')
            ? setSelectedChartList(localDefault.selectedChartList)
            : setSelectedChartList([chartList[3], chartList[4]]); // 설비가동률-chartList[3], 설비상태-chartList[4]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    // selectedList localStorage에 저장
    useEffect(() => {
        if (testcaseId) {
            let localData = { 'selectedFacilityList': selectedFacilityList, 'selectedProductList': selectedProductList, 'selectedChartList': selectedChartList };
            localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(localData));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFacilityList, selectedProductList, selectedChartList]);

    const handleSelectChange = (value, prev, func) => {
        prev.includes(value) ? func(prev.filter(item => item != value)) : func([...prev, value]);
    }

    // timeline
    useEffect(() => {
        if (resultState !== undefined && resultState.length !== 0 && selectedFacilityList.length !== 0) {
            let stateDataFacilities = [];

            resultState.forEach((item) => {
                if (selectedFacilityList.includes(item.facilityId)) {
                    stateDataFacilities.push(item);
                }
            });
            let tempList = stateDataFacilities.map((item) => {
                const endTime = item.endTime ? item.endTime : testcase.simulateTime;
                return [item.name, item.state, new Date(0, 0, 0, 0, 0, item.startTime), new Date(0, 0, 0, 0, 0, endTime)];
            });
            //tempList.sort((a, b) => a[0] === b[0] && a[2] < b[2] ? -1 : 1);
            let timeline = [
                [{ type: 'string', id: 'name' }, { type: 'string', id: 'state' }, { type: 'date', id: 'start' }, { type: 'date', id: 'end' }],
                ...tempList
            ];
            let colors = timeline.filter((item, index) => index != 0).map(item => timelineColor[stateList.indexOf(item[1])]);
            setTimelineColorOrder([...new Set(colors)]);
            setTimelineData(timeline);
        }
    }, [facilityList, selectedFacilityList, resultState]);

    // 설비 관련 chart
    useEffect(() => {
        if (resultFacility !== undefined && resultFacility.length !== 0 && selectedFacilityList.length !== 0) {
            let facilityDataCols = ['time'];
            let facilityDataHeads = [{ label: '시간', type: 'timeofday' }];
            let facilityDataTimes = [];

            resultFacility.sort((a, b) => (a.name > b.name) ? 1 : (a.name === b.name) ? ((a.time > b.time) ? 1 : -1) : -1);
            resultFacility.forEach((item) => {
                if (!facilityDataCols.includes(item.facilityId)) {
                    if (selectedFacilityList.includes(item.facilityId)) {
                        facilityDataCols.push(item.facilityId);
                        facilityDataHeads.push(item.name);
                    }
                }
                if (!facilityDataTimes.includes(item.time)) {
                    if (selectedFacilityList.includes(item.facilityId)) {
                        facilityDataTimes.push(item.time);
                    }
                }
            });

            const makeChartData = function (columnName, interval, calcMean) {
                let temp = [];
                let counts = [];
                const times = interval ? [... new Set(facilityDataTimes.map(item => parseInt((item - 1) / interval) * interval + interval))] : facilityDataTimes;
                times.forEach((item) => {
                    temp.push([item, ...Array(facilityDataCols.length - 1).fill(0)]);
                    counts.push([0, ...Array(facilityDataCols.length - 1).fill(0)]);
                });
                resultFacility.forEach((item) => {
                    if (interval) {
                        const time = parseInt((item.time - 1) / interval) * interval + interval;
                        temp[times.indexOf(time)][facilityDataCols.indexOf(item.facilityId)] += item[columnName];
                        counts[times.indexOf(time)][facilityDataCols.indexOf(item.facilityId)] += 1;
                    } else {
                        temp[times.indexOf(item.time)][facilityDataCols.indexOf(item.facilityId)] = item[columnName];
                        counts[times.indexOf(item.time)][facilityDataCols.indexOf(item.facilityId)] += 1;
                    }
                });
                if (calcMean) {
                    temp = temp.map((row, rowIdx) => row.map((item, colIdx) => counts[rowIdx][colIdx] === 0 ? item : item / counts[rowIdx][colIdx]));
                } else {
                    temp.map(row => row.filter((col, index) => { if (index !== 0) row[index] = col * 100; }));
                }
                return [facilityDataHeads, ...temp.map(row => [[0, 0, row[0]], ...row.filter((col, index) => index !== 0)])];
            }
            setWipData(makeChartData("wip", rangeValue * 60, true));
            setWorkRatio(makeChartData("workRatio"));
            //setLoadFactor(makeChartData("loadFactor"));
            // setLossTime(makeChartData("lossTime"));
            setLeadTime(makeChartData("leadTime", rangeValue * 60, true));
        }
    }, [selectedFacilityList, resultFacility, rangeValue]);

    useEffect(() => {
        setResultProduct(getResultProduct(result.resultProduct));
    }, [rangeValue])

    // resultState
    useEffect(() => {
        if (resultState !== undefined && resultState.length !== 0 && selectedFacilityList.length !== 0) {
            let data = {};
            resultState.forEach((item) => {
                if (selectedFacilityList.includes(item.facilityId)) {
                    if (!data[item.name]) {
                        data[item.name] = {};
                    }
                    let prevValue = data[item.name][item.state];
                    data[item.name][item.state] = (prevValue ? prevValue : 0) + (item.endTime - item.startTime);
                }
            });
            let list = Object.entries(data).map((item) => {
                return [item[0], ...stateList.map(state => item[1][state] ? item[1][state] : 0)];
            });
            setStateData([['설비명', ...stateList], ...list]);
        }
    }, [facilityList, selectedFacilityList, resultState]);

    // 상품 생산량 데이터
    const getResultProduct = function (resultData) {
        if (resultData) {
            const interval = rangeValue * 60;
            let productDataHeads = [{ label: '시간', type: 'timeofday' }];
            let productDataTimes = [];
            resultData.sort((a, b) => (a.name > b.name) ? 1 : (a.name === b.name) ? ((a.time > b.time) ? 1 : -1) : -1);

            resultData.forEach((item) => {
                if (!productDataHeads.includes(item.name)) {
                    productDataHeads.push(item.name);
                    productDataHeads.push(item.name + "(누적)");
                }
                const time = parseInt((item.time - 1) / interval) * interval + interval;
                if (!productDataTimes.includes(time)) {
                    productDataTimes.push(time);
                }
            });

            let tempList = productDataTimes.map(item =>
                [[0, 0, item], ...Array(productDataHeads.length - 1).fill(0)]
            );

            let accValue = 0;
            let prevName = "";
            resultData.forEach((item) => {
                if (prevName !== item.name) {
                    accValue = 0;
                }
                const time = parseInt((item.time - 1) / interval) * interval + interval;
                tempList[productDataTimes.indexOf(time)][productDataHeads.indexOf(item.name)] += item["quantity"];
                accValue += item["quantity"];
                tempList[productDataTimes.indexOf(time)][productDataHeads.indexOf(item.name + "(누적)")] = accValue;
                prevName = item.name;
            });
            return [productDataHeads, ...tempList];
        }
        return [];
    }
    // resultProduct
    useEffect(() => {
        if (resultProduct.length > 0 && selectedProductList) {
            const included = resultProduct[0].map((item, index) => index === 0 || selectedProductList.includes(item.replace('(누적)', '')));
            setProductData(resultProduct.map((row) => {
                return row.filter((item, index) => {
                    return included[index];
                });
            }));
        }
    }, [resultProduct, selectedProductList]);

    const getResultTime = function (resultData) {
        if (resultData) {
            let timeDataHeads = ['제품명', '소요시간', 'dummy', 'dummy', 'dummy', { role: "tooltip", type: "string" }];
            let stat = {};

            resultData.sort((a, b) => (a.name > b.name) ? 1 : (a.name === b.name) ? ((a.time > b.time) ? 1 : -1) : -1);

            resultData.forEach((item) => {
                if (!stat[item.name]) {
                    stat[item.name] = { min: item.time, max: item.time, list: [item.time] }
                } else {
                    let temp = stat[item.name];
                    stat[item.name] = {
                        min: temp.min > item.time ? item.time : temp.min,
                        max: temp.max < item.time ? item.time : temp.max,
                        list: [...temp.list, item.time]
                    }
                }
            });
            return [timeDataHeads, ...
                Object.entries(stat).map(([name, item]) => {
                    return [name, item.min, item.list[parseInt(item.list.length / 4)], item.list[parseInt(item.list.length / 4 * 3)], item.max];
                }).map(item => [...item, '최소 : ' + item[1] + ', 25% : ' + item[2] + ', 75% : ' + item[3] + ', 최대 : ' + item[4]])
            ];
        }
        return [];
    }
    // resultTime
    useEffect(() => {
        if (resultTime.length > 0 && selectedProductList) {
            setTimeData(resultTime.filter((item, index) => index === 0 || selectedProductList.includes(item[0])));
        }
    }, [resultTime, selectedProductList]);
    // KPI
    useEffect(() => {
        if (result.resultFacility && result.resultProduct && result.resultTime) {
            let wipAll = 0;
            result.resultFacility.filter(item => item.time === testcase.simulateTime).forEach(item => {
                wipAll += item.wip;
            });
            let outputAll = 0;
            let statistic = {};
            let times = [];
            result.resultProduct.forEach(item => {
                outputAll += item.quantity;
                if (!statistic[item.name]) {
                    statistic[item.name] = { 'productName': item.name, 'outputSum': 0, 'outputTime': 0, 'leadTime': 0, 'leadCount': 0 };
                }
                statistic[item.name].outputSum += item.quantity;
                times.push(item.time);
            });
            result.resultTime.forEach(item => {
                statistic[item.name].leadTime += item.time * item.quantity;
                statistic[item.name].leadCount += item.quantity;
            })
            let productList = [];
            for (let key in statistic) {
                statistic[key].outputTime = (statistic[key].outputSum / (testcase.simulateTime - testcase.logTime) * 3600).toFixed(2); // 분당 생산량
                statistic[key].leadTime = ((statistic[key].leadTime) / statistic[key].leadCount).toFixed(2);
                productList.push(statistic[key]);
            }
            // 임시로 범위를 다른 데이터에서 가져옴
            axios.get('data/task/all/' + testcaseId).then(response => {
                const percent = !response.data ? 0.05 : (response.data.reduce((value, item) => value + parseFloat(item.reqTimeDev), 0) / response.data.length / 100);
                const [min, max] = [1 - percent, 1 + percent];
                setKpiData({
                    outputAll: parseInt(outputAll * min) + ' ~ ' + parseInt(outputAll * max),
                    wipAll: parseInt(wipAll * min) + ' ~ ' + parseInt(wipAll * max),
                    productList: productList.map(item => ({
                        productName: item.productName,
                        outputSum: item.outputSum,
                        outputTime: item.outputTime,
                        leadTime: item.leadTime,
                    }))
                });
            });
        }
    }, [result]);

    useEffect(() => {
        if (stateData.length === 0) {
            return;
        }

        const workRatioAve = stateData.filter((item, index) => index !== 0)
            .map(item => item[2])
            .reduce((accum, value) => accum + (value / (stateData.length - 1) * 1.7), 0);

        setOverWorkList(stateData.filter((item, index) => index !== 0 && item[2] > workRatioAve).map(item => item[0]));
        setDelayList(stateData.filter((item, index) => index !== 0 && item[5] >= (testcase.simulateTime - testcase.logTime) / 10).map(item => item[0]));
    }, [stateData]);

    useEffect(() => {
        if (leadTime.length === 0) {
            return;
        }
        let tempList = [...leadTime];
        let facilityList = tempList.shift().slice(1);
        let meanList = [];
        let stddevList = [];

        facilityList.map((col, colIdx) => { // 설비별 leadTime 평균
            let temp = 0;
            tempList.map((row, rowIdx) => { temp += tempList[rowIdx][colIdx + 1]; });
            meanList[colIdx] = temp / tempList.length;
        });
        meanList.map((col, colIdx) => {
            let temp = 0;
            tempList.map((row, rowIdx) => { temp += tempList[rowIdx][colIdx + 1] - col; });
            stddevList[colIdx] = Math.sqrt((temp * temp) / (tempList.length - 1));
        });
        setOverCoefList(facilityList.filter((item, index) => 0.3 < stddevList[index] / meanList[index]));

    }, [leadTime]);

    useEffect(() => {
        if (selectedFacilityList.length !== 0) {
            setTimelineHeight(selectedFacilityList.length * 41 + 50);
        }
    }, [selectedFacilityList]);

    const drawChart = function (title, tooltip, data, type, options) {
        return <GridItem md={(selectedChartList && selectedChartList.includes(title) ? 6 : 4)} xs={12} key={title}>
            <Card id={title}>
                <CardHeader>
                    <h4 style={{ display: 'inline', marginRight: 20 }}><b>{title}</b></h4>
                    <Tooltip arrow title={tooltip} placement="right" classes={{ tooltip: classes.tooltip }}>
                        <Button simple justIcon>
                            <img src={InfoIcon} width='40' height='20' />
                        </Button>
                    </Tooltip>
                </CardHeader>
                <CardBody toTop>
                    <GridContainer>
                        <GridItem md={12}>
                            {type === 'CandlestickChart' ?
                                <Chart chartType="CandlestickChart" loader={<div>Loading Chart</div>} legendToggle data={data}
                                    options={{
                                        legend: { position: 'top', maxLines: 3 },
                                        chartArea: { width: '85%' },
                                        explorer: {
                                            actions: ['dragToZoom', 'rightClickToReset'],
                                            axis: 'horizontal', keepInBounds: true, maxZoomIn: 4.0
                                        },
                                        ...options
                                    }}
                                />
                                : <Chart chartType="ComboChart" loader={<div>Loading Chart</div>} legendToggle data={data}
                                    options={{
                                        seriesType: type ? type : 'line',
                                        legend: { position: 'top', maxLines: 3 },
                                        chartArea: { width: '85%' },
                                        explorer: {
                                            actions: ['dragToZoom', 'rightClickToReset'],
                                            axis: 'horizontal', keepInBounds: true, maxZoomIn: 4.0
                                        },
                                        ...options
                                    }}
                                />
                            }

                        </GridItem>
                    </GridContainer>
                </CardBody>
            </Card>
        </GridItem>;
    };

    return (
        <>
            <GridContainer style={{ padding: '30px 10px' }}>
                <GridItem xs={6} md={3} className={classes.center} style={{ margin: 'auto' }}>
                    <h4 style={{ display: "inline" }}><b>{testcase.name}</b></h4>
                    <h6 style={{ display: "inline", marginLeft: 10 }}>{testcase.createDate}</h6>
                </GridItem>
                <GridItem xs={6} md={2}>
                    <Typography variant='body2' color='textSecondary'>전체 생산 시간</Typography>
                    <div>
                        <Typography variant='h5' style={{ color: '#00acc1', fontWeight: 'bold' }} display="inline">
                            {((result.simulateTime - testcase.logTime) / 3600).toFixed(2)}
                        </Typography>
                        <Typography variant='caption' color='textSecondary' display="inline"> Hour</Typography>
                    </div>
                </GridItem>
                <GridItem xs={6} md={2}>
                    <Typography variant='body2' color='textSecondary'>전체 상품 생산량</Typography>
                    <div>
                        <Typography variant='h5' style={{ color: '#00acc1', fontWeight: 'bold' }} display="inline">
                            {kpiData.outputAll}
                        </Typography>
                        <Typography variant='caption' color='textSecondary' display="inline"> 개</Typography>
                    </div>
                </GridItem>
                <GridItem xs={6} md={2}>
                    <Typography variant='body2' color='textSecondary'>재공재고</Typography>
                    <div>
                        <Typography variant='h5' style={{ color: '#00acc1', fontWeight: 'bold' }} display="inline">
                            {kpiData.wipAll}
                        </Typography>
                        <Typography variant='caption' color='textSecondary' display="inline"> 개</Typography>
                    </div>
                </GridItem>
                <GridItem xs={6} md={2}>
                    {/*<Button color='infoOutline' className={classes.right}>레포트 보기</Button>*/}
                </GridItem>
            </GridContainer>
            <GridContainer>
                <GridItem md={6} xs={12}>
                    <UnityInterface testcaseId={testcaseId} testcase={testcase} showResult scenarioId={testcase.scenarioId} height={"400px"} onFullScreen={isFull => { setUnityFull(isFull) }} />
                </GridItem>
                {resultFacility === undefined || isUnityFull ?
                    <GridItem md={12} xs={12} className={classes.center}>
                        <h4><b>등록된 시뮬레이션 결과가 없습니다.</b></h4>
                    </GridItem> :
                    <>
                        <GridItem md={6} xs={12}>
                            <Card>
                                <CardHeader>
                                    <h4 style={{ display: 'inline', marginRight: 20 }}><b>생산요약</b></h4>
                                    <Tooltip arrow
                                        title="전체 생산 시간 동안 공정의 생산 결과 요약입니다. 실제 고객사 현황과 맞는지 확인하세요."
                                        placement="right"
                                        classes={{ tooltip: classes.tooltip }}>
                                        <Button simple justIcon>
                                            <img src={InfoIcon} width='40' height='20' />
                                        </Button>
                                    </Tooltip>
                                </CardHeader>
                                <CardBody toTop>
                                    <GridContainer>
                                        <GridItem xs={12}>
                                            <Table striped tableHeaderColor="primary"
                                                tableHead={["제품 명", "누적 생산량(개)", "시간당 생산량(개/h)", '제조 리드타임(s)']}
                                                tableCols={["productName", "outputSum", "outputTime", 'leadTime']}
                                                tableData={kpiData.productList}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </CardBody>
                            </Card>
                            {delayList.length === 0 ? null :
                                <SnackbarContent
                                    message={delayList.join(', ') + ' 설비의 정체 비율이 높아 병목구간일 수 있습니다. 설비상태와 재공재고를 확인하세요.'}
                                    color="danger"
                                    icon={AddAlert}
                                />}
                            {overWorkList.length === 0 ? null :
                                <SnackbarContent
                                    message={overWorkList.join(', ') + ' 설비의 가동률이 평균보다 높아 생산량에 영향을 미칠 수 있습니다.'}
                                    color="danger"
                                    icon={AddAlert}
                                />}
                            {overCoefList.length === 0 ? null :
                                <SnackbarContent
                                    message={overCoefList.join(', ') + ' 설비의 생산량 변동성이 큽니다. 로봇 또는 신규 설비 도입을 추천합니다.'}
                                    color="danger"
                                    icon={AddAlert}
                                />}
                        </GridItem>
                        <GridItem xs={12} md={12}>
                            <Card>
                                <CardHeader>
                                    <h4 style={{ display: 'inline', marginRight: 20 }}><b>생산 결과</b></h4>
                                    <Tooltip arrow
                                        title="시간별 설비의 동작 상태와 생산 제품을 나타내는 간트 차트입니다."
                                        placement="right"
                                        classes={{ tooltip: classes.tooltip }} >
                                        <Button simple justIcon>
                                            <img src={InfoIcon} width='40' height='20' />
                                        </Button>
                                    </Tooltip>
                                </CardHeader>
                                <CardBody toTop>
                                    <GridContainer>
                                        <GridItem xs={12} md={12}>
                                            <div className={classes.right}>
                                                {stateList.map((item, index) => (
                                                    <span style={{ marginLeft: '10px' }} key={item}>
                                                        <span style={{ color: timelineColor[index], fontSize: '1.4em' }}>■</span>
                                                        <span style={{ color: '#343434', fontSize: '12px' }}> {item}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </GridItem>
                                        <GridItem xs={12} md={12} style={{ height: timelineHeight }}>
                                            <Chart chartType="Timeline" loader={<div>Loading Chart</div>}
                                                data={timelineData} options={{
                                                    colors: timelineColorOrder,
                                                    height: timelineHeight,
                                                    hAxis: {
                                                        format: 'HH:mm:ss',
                                                    }
                                                }}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </CardBody>
                            </Card>
                        </GridItem>
                        <GridItem xs={12} md={12}>
                            <Card>
                                <CardBody style={{ backgroundColor: '#d9edf7' }}>
                                    <GridContainer>
                                        <b style={{ display: 'inline', marginLeft: 40, marginRight: 40, marginTop: 5, lineHeight: '60px' }}>결과 자세히 보기</b>
                                        <GridItem md={2}>
                                            <CheckboxSelect fullWidth title="facility" items={facilityList.map(item => ({ name: item.name, value: item.facilityId }))}
                                                handleItemClick={value => handleSelectChange(value, selectedFacilityList, setSelectedFacilityList)}
                                                selectedItemList={selectedFacilityList} />
                                        </GridItem>
                                        <GridItem md={2}>
                                            <CheckboxSelect fullWidth title="product" items={productList.map(item => ({ name: item, value: item }))}
                                                handleItemClick={value => handleSelectChange(value, selectedProductList, setSelectedProductList)}
                                                selectedItemList={selectedProductList} />
                                        </GridItem>
                                        <GridItem md={2}>
                                            <CheckboxSelect fullWidth title="chart" items={chartList.map(item => ({ name: item, value: item }))}
                                                handleItemClick={value => handleSelectChange(value, selectedChartList, setSelectedChartList)}
                                                selectedItemList={selectedChartList} />
                                        </GridItem>
                                        <GridItem md={4}>
                                            <h6 style={{ color: "#AAA" }}>chart range</h6>
                                            <div className={classes.checkboxAndRadio + " " + classes.checkboxAndRadioHorizontal}>
                                                {[1, 5, 15].map(item =>
                                                    <FormControlLabel key={item}
                                                        classes={{ label: classes.label, root: classes.labelRoot }}
                                                        label={item + "분"}
                                                        control={
                                                            <Radio checked={rangeValue === item} onChange={e => setRangeValue(parseInt(e.target.value))}
                                                                value={item} icon={<FiberManualRecord className={classes.radioUnchecked} />}
                                                                checkedIcon={<FiberManualRecord className={classes.infoRadioChecked} />}
                                                                classes={{ checked: classes.infoRadio, root: classes.radioRoot }} />
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </GridItem>
                                    </GridContainer>
                                </CardBody>
                            </Card>
                        </GridItem>
                        {
                            [
                                ['상품 생산량', drawChart('상품 생산량', '시간별 제품의 생산 수량으로, 높을수록 좋습니다.', productData, 'ComboChart', { vAxis: { title: '갯수', titleTextStyle: { italic: false, fontSize: 13 } } })],
                                ['생산 소요시간', drawChart('생산 소요시간', '제품별 제조 리드타임으로, 리드타임이 짧을 수록 좋습니다.', timeData, 'CandlestickChart', { vAxis: { title: '시간(초)', titleTextStyle: { italic: false, fontSize: 13 } } })],
                                ['재공재고', drawChart('재공재고', '시간별 설비의 재공재고 변화로, 누적이 지속될 수록 병목의 확률이 높습니다.', wipData, 'ComboChart', { vAxis: { title: '갯수', titleTextStyle: { italic: false, fontSize: 13 } } })],
                                ['설비가동률', drawChart('설비가동률', '시간별 설비의 가동률 변화로, 높을수록 좋습니다.', workRatio, 'ComboChart', { vAxis: { title: '가동률(%)', titleTextStyle: { italic: false, fontSize: 13 } } })],
                                ['설비상태', drawChart('설비상태', '설비의 대기, 세팅, 가동, 오류 등 동작 상태 비율입니다.', stateData, 'bars',
                                    { isStacked: true, colors: timelineColor, vAxis: { title: '시간(초)', titleTextStyle: { italic: false, fontSize: 13 } } })],
                                // ['설비부하율', drawChart('설비부하율', '시간별 설비의 작업량 변화로, 설비간 균일할 수록 좋습니다.', loadFactor)],
                                // ['설비 Loss time', drawChart('설비 Loss time', '시간별 설비의 대기, 오류 등 손실 시간 변화입니다.', lossTime)],
                                ['단위 제품당 처리시간', drawChart('단위 제품당 처리시간', '시간별 설비의 단위 제품당 처리시간 변화입니다.', leadTime, 'ComboChart', { vAxis: { title: '시간(초)', titleTextStyle: { italic: false, fontSize: 13 } } })],
                            ].sort((a, b) => selectedChartList && selectedChartList.includes(a[0]) ? -1 : 1).map(item => { return item[1] })
                        }
                    </>}
            </GridContainer>
        </>
    );
}

ResultViewPage.propTypes = {
    testcaseId: PropTypes.number,
    result: PropTypes.any
};